import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  Check,
  CheckCircle2,
  Coins,
  Eye,
  Globe,
  Mail,
  MessageCircle,
  MessageSquare,
  Moon,
  Pencil,
  Repeat,
  Send,
  Smartphone,
  Sparkles,
  Users,
  XCircle,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { TIME_ZONE_OPTIONS } from "../../programs/mock-programs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useActiveProgramId } from "../../programs/program-scope";
import { ProgramsApi, programKeys } from "@/api/perkpoint/programs";
import { InsightsApi, insightKeys } from "@/api/perkpoint/insights";
import {
  CampaignsApi,
  campaignKeys,
  isCampaignEditable,
} from "@/api/perkpoint/campaigns";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { ChannelPreview } from "./ChannelPreview";
import {
  ALL_MEMBERS,
  CAMPAIGN_TYPES,
  CHANNELS,
  OFFER_EVENTS,
  STEPS,
  TRIGGER_EVENTS,
  VARIABLES,
  bonusLabel,
  draftFromApiCampaign,
  draftToCampaignPayload,
  emptyDraft,
  estimateAudience,
  estimateCost,
  nextCampaignCode,
  programContext,
  reviewChecks,
  sampleValues,
  stepErrors,
  type CampaignDraft,
  type ChannelKey,
  type ProgramContext,
  type StepId,
} from "./model";

const CHANNEL_ICONS: Record<ChannelKey, typeof Mail> = {
  whatsapp: MessageCircle,
  email: Mail,
  sms: MessageSquare,
  inapp: Smartphone,
  web: Globe,
};

const fieldClass = "h-10 bg-white";

function fmt(value: number) {
  return Math.round(value).toLocaleString("en-IN");
}

function Field({
  label,
  hint,
  children,
  required,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="space-y-5 p-5 sm:p-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          {description ? <p className="mt-0.5 text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function ChoiceCard({
  active,
  onClick,
  title,
  hint,
  icon: Icon,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  hint?: string;
  icon?: typeof Mail;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "relative flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-all",
        active
          ? "border-green-600 bg-green-50/70 ring-1 ring-green-600"
          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50",
      )}
    >
      {Icon ? (
        <span
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
            active ? "bg-green-600 text-white" : "bg-slate-100 text-slate-600",
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
      ) : null}
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        {hint ? <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span> : null}
      </span>
      {active ? (
        <span className="absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-white">
          <Check className="h-3 w-3" />
        </span>
      ) : null}
    </button>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "border-green-600 bg-green-600 text-white"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
      )}
    >
      {children}
    </button>
  );
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

/* ------------------------------------------------------------------ */
/* Steps                                                               */
/* ------------------------------------------------------------------ */

type StepProps = {
  draft: CampaignDraft;
  update: (fn: (draft: CampaignDraft) => void) => void;
  ctx: ProgramContext;
};

function BasicsStep({ draft, update, ctx, nextCode }: StepProps & { nextCode: string }) {
  const o = draft.offer;
  return (
    <div className="space-y-6">
      <Section title="What is this campaign?" description="Members never see the name. Your team does.">
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_160px]">
          <Field label="Campaign name" required>
            <Input
              className={fieldClass}
              value={draft.name}
              placeholder="e.g. Q4 win-back weekend bonus"
              onChange={(e) => update((d) => void (d.name = e.target.value))}
            />
          </Field>
          <Field
            label="Campaign ID"
            hint={draft.code ? "Assigned when created" : "Assigned when you save"}
          >
            <div className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 font-mono text-sm text-slate-600">
              {draft.code || nextCode}
            </div>
          </Field>
        </div>
        <Field label="Description">
          <Textarea
            className="min-h-[72px] bg-white"
            value={draft.description}
            placeholder="What it is for, in one line"
            onChange={(e) => update((d) => void (d.description = e.target.value))}
          />
        </Field>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">
            Type<span className="text-red-500"> *</span>
          </Label>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {CAMPAIGN_TYPES.map((type) => (
              <ChoiceCard
                key={type.value}
                active={draft.type === type.value}
                onClick={() => update((d) => void (d.type = type.value))}
                title={type.label}
                hint={type.hint}
              />
            ))}
          </div>
        </div>
      </Section>

      <Section title="Reward" description="Optional. Creates a points rule that runs for the campaign dates.">
        <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center gap-3">
            <Coins className="h-5 w-5 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-slate-900">Give bonus {ctx.pointsName}</p>
              <p className="text-xs text-muted-foreground">Turn off for a message-only campaign</p>
            </div>
          </div>
          <Switch
            checked={o.enabled}
            onCheckedChange={(checked) => update((d) => void (d.offer.enabled = checked))}
            aria-label="Give bonus points"
          />
        </div>
        {o.enabled ? (
          <div className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-2">
              <ChoiceCard
                active={o.kind === "multiply"}
                onClick={() => update((d) => void (d.offer.kind = "multiply"))}
                title="Multiply points"
                hint="e.g. 2x on every booking"
                icon={Zap}
              />
              <ChoiceCard
                active={o.kind === "fixed"}
                onClick={() => update((d) => void (d.offer.kind = "fixed"))}
                title="Fixed bonus"
                hint="e.g. 500 points once"
                icon={Sparkles}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {o.kind === "multiply" ? (
                <Field label="Multiplier" required>
                  <div className="flex items-center gap-2">
                    <Input
                      className={fieldClass}
                      type="number"
                      min={1}
                      step="0.25"
                      value={o.multiplier}
                      onChange={(e) => update((d) => void (d.offer.multiplier = e.target.value))}
                    />
                    <span className="text-sm text-muted-foreground">x base earn</span>
                  </div>
                </Field>
              ) : (
                <Field label="Bonus points" required>
                  <Input
                    className={fieldClass}
                    type="number"
                    min={1}
                    value={o.points}
                    onChange={(e) => update((d) => void (d.offer.points = e.target.value))}
                  />
                </Field>
              )}
              <Field label="When">
                <Select value={o.event} onValueChange={(v) => update((d) => void (d.offer.event = v))}>
                  <SelectTrigger className={fieldClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OFFER_EVENTS.map((event) => (
                      <SelectItem key={event.value} value={event.value}>
                        {event.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Cap per member" hint="Most bonus points one member can get">
                <Input
                  className={fieldClass}
                  type="number"
                  min={0}
                  value={o.capPerMember}
                  placeholder="No cap"
                  onChange={(e) => update((d) => void (d.offer.capPerMember = e.target.value))}
                />
              </Field>
              <Field
                label="Total budget"
                hint={
                  Number(o.budgetPoints) > 0
                    ? `≈ ${ctx.currency} ${fmt(Number(o.budgetPoints) * ctx.costPerPoint)} at ${ctx.costPerPoint} per point`
                    : "The rule stops when the budget runs out"
                }
              >
                <Input
                  className={fieldClass}
                  type="number"
                  min={0}
                  value={o.budgetPoints}
                  placeholder="No budget limit"
                  onChange={(e) => update((d) => void (d.offer.budgetPoints = e.target.value))}
                />
              </Field>
            </div>
          </div>
        ) : null}
      </Section>
    </div>
  );
}

function AudienceStep({ draft, update, ctx }: StepProps) {
  const estimate = estimateAudience(draft, ctx);
  const a = draft.audience;
  const segmentChoices = [
    { id: ALL_MEMBERS, label: "All members", members: ctx.totalMembers },
    ...ctx.segments,
  ];
  return (
    <div className="space-y-6">
      <Section title="Who should get it?" description="Pick one or more segments. Members in any of them are included.">
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {segmentChoices.map((segment) => (
            <ChoiceCard
              key={segment.id}
              active={a.segments.includes(segment.id)}
              onClick={() =>
                update((d) => {
                  d.audience.segments =
                    segment.id === ALL_MEMBERS
                      ? d.audience.segments.includes(ALL_MEMBERS) ? [] : [ALL_MEMBERS]
                      : toggle(d.audience.segments.filter((s) => s !== ALL_MEMBERS), segment.id);
                })
              }
              title={segment.label}
              hint={`${fmt(segment.members)} members`}
              icon={Users}
            />
          ))}
        </div>
        {a.segments.length === 0 ? (
          <p className="text-xs text-muted-foreground">No segment picked, so every member is included.</p>
        ) : null}
      </Section>

      <Section title="Narrow by tier" description="Optional.">
        {ctx.tiers.length === 0 ? (
          <p className="text-sm text-muted-foreground">This program has no tiers yet.</p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Only these tiers</Label>
              <div className="flex flex-wrap gap-2">
                {ctx.tiers.map((tier) => (
                  <Chip
                    key={tier.id}
                    active={a.includeTiers.includes(tier.id)}
                    onClick={() =>
                      update((d) => {
                        d.audience.includeTiers = toggle(d.audience.includeTiers, tier.id);
                        d.audience.excludeTiers = d.audience.excludeTiers.filter((t) => t !== tier.id);
                      })
                    }
                  >
                    {tier.name}
                  </Chip>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Leave out these tiers</Label>
              <div className="flex flex-wrap gap-2">
                {ctx.tiers.map((tier) => (
                  <Chip
                    key={tier.id}
                    active={a.excludeTiers.includes(tier.id)}
                    onClick={() =>
                      update((d) => {
                        d.audience.excludeTiers = toggle(d.audience.excludeTiers, tier.id);
                        d.audience.includeTiers = d.audience.includeTiers.filter((t) => t !== tier.id);
                      })
                    }
                  >
                    {tier.name}
                  </Chip>
                ))}
              </div>
            </div>
          </div>
        )}
      </Section>

      <Section title="Control group" description="Held-out members get nothing, so you can measure the lift.">
        <div className="flex items-center gap-4">
          <Slider
            value={[a.controlPct]}
            min={0}
            max={30}
            step={5}
            onValueChange={([value]) => update((d) => void (d.audience.controlPct = value))}
            className="flex-1"
            aria-label="Control group percentage"
          />
          <span className="w-14 text-right text-sm font-semibold tabular-nums text-slate-900">{a.controlPct}%</span>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          {[
            ["Members matched", estimate.matched],
            ["− Left out by tier", estimate.tierExcluded],
            [`− Control group (${a.controlPct}%)`, estimate.control],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between py-1 text-sm">
              <span className="text-slate-600">{label}</span>
              <span className="tabular-nums text-slate-800">{fmt(value as number)}</span>
            </div>
          ))}
          <div className="mt-1 flex justify-between border-t border-slate-200 pt-2 text-sm font-semibold">
            <span>Campaign audience</span>
            <span className="tabular-nums text-green-700">{fmt(estimate.audience)}</span>
          </div>
        </div>
      </Section>
    </div>
  );
}

function VariableChips({ onInsert }: { onInsert: (token: string) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">Insert:</span>
      {VARIABLES.map((variable) => (
        <button
          key={variable.key}
          type="button"
          onClick={() => onInsert(`{{${variable.key}}}`)}
          className="rounded-md border border-dashed border-green-300 bg-green-50 px-2 py-0.5 font-mono text-[11px] text-green-800 hover:bg-green-100"
        >
          {variable.label}
        </button>
      ))}
    </div>
  );
}

function ChannelEditor({ channel, draft, update, ctx }: StepProps & { channel: ChannelKey }) {
  const c = draft.content;
  const append = (fn: (d: CampaignDraft, token: string) => void) => (token: string) =>
    update((d) => fn(d, token));

  if (channel === "whatsapp") {
    return (
      <div className="space-y-4">
        <Field label="Approved template name" required hint="Must match the template approved in WhatsApp Business">
          <Input className={fieldClass} value={c.whatsapp.template} placeholder="winback_q4_v1"
            onChange={(e) => update((d) => void (d.content.whatsapp.template = e.target.value))} />
        </Field>
        <Field label="Header">
          <Input className={fieldClass} value={c.whatsapp.header} placeholder="Optional bold first line"
            onChange={(e) => update((d) => void (d.content.whatsapp.header = e.target.value))} />
        </Field>
        <Field label="Message" required>
          <Textarea className="min-h-[120px] bg-white" value={c.whatsapp.body}
            onChange={(e) => update((d) => void (d.content.whatsapp.body = e.target.value))} />
        </Field>
        <VariableChips onInsert={append((d, t) => void (d.content.whatsapp.body += ` ${t}`))} />
        <Field label="Footer">
          <Input className={fieldClass} value={c.whatsapp.footer}
            onChange={(e) => update((d) => void (d.content.whatsapp.footer = e.target.value))} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Button label">
            <Input className={fieldClass} value={c.whatsapp.buttonLabel}
              onChange={(e) => update((d) => void (d.content.whatsapp.buttonLabel = e.target.value))} />
          </Field>
          <Field label="Button link">
            <Input className={fieldClass} value={c.whatsapp.buttonUrl} placeholder="https://"
              onChange={(e) => update((d) => void (d.content.whatsapp.buttonUrl = e.target.value))} />
          </Field>
        </div>
      </div>
    );
  }
  if (channel === "sms") {
    return (
      <div className="space-y-4">
        <Field label="Sender ID" hint="Set in program configuration">
          <Input className={fieldClass} value={ctx.smsSender} disabled />
        </Field>
        <Field label="Text" required hint="Keep it to one part (160 characters) where you can">
          <Textarea className="min-h-[120px] bg-white" value={c.sms.body}
            onChange={(e) => update((d) => void (d.content.sms.body = e.target.value))} />
        </Field>
        <VariableChips onInsert={append((d, t) => void (d.content.sms.body += ` ${t}`))} />
      </div>
    );
  }
  if (channel === "email") {
    return (
      <div className="space-y-4">
        <Field label="From name">
          <Input className={fieldClass} value={c.email.fromName}
            onChange={(e) => update((d) => void (d.content.email.fromName = e.target.value))} />
        </Field>
        <Field label="Subject" required>
          <Input className={fieldClass} value={c.email.subject} placeholder="{{first_name}}, double points this weekend"
            onChange={(e) => update((d) => void (d.content.email.subject = e.target.value))} />
        </Field>
        <Field label="Preview text" hint="Shown next to the subject in most inboxes">
          <Input className={fieldClass} value={c.email.preheader}
            onChange={(e) => update((d) => void (d.content.email.preheader = e.target.value))} />
        </Field>
        <Field label="Heading">
          <Input className={fieldClass} value={c.email.heading}
            onChange={(e) => update((d) => void (d.content.email.heading = e.target.value))} />
        </Field>
        <Field label="Body" required>
          <Textarea className="min-h-[140px] bg-white" value={c.email.body}
            onChange={(e) => update((d) => void (d.content.email.body = e.target.value))} />
        </Field>
        <VariableChips onInsert={append((d, t) => void (d.content.email.body += ` ${t}`))} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Button label">
            <Input className={fieldClass} value={c.email.ctaLabel}
              onChange={(e) => update((d) => void (d.content.email.ctaLabel = e.target.value))} />
          </Field>
          <Field label="Button link">
            <Input className={fieldClass} value={c.email.ctaUrl} placeholder="https://"
              onChange={(e) => update((d) => void (d.content.email.ctaUrl = e.target.value))} />
          </Field>
        </div>
      </div>
    );
  }
  if (channel === "inapp") {
    return (
      <div className="space-y-4">
        <Field label="Title" required>
          <Input className={fieldClass} value={c.inapp.title} placeholder="Double points are on"
            onChange={(e) => update((d) => void (d.content.inapp.title = e.target.value))} />
        </Field>
        <Field label="Text">
          <Textarea className="min-h-[96px] bg-white" value={c.inapp.body}
            onChange={(e) => update((d) => void (d.content.inapp.body = e.target.value))} />
        </Field>
        <VariableChips onInsert={append((d, t) => void (d.content.inapp.body += ` ${t}`))} />
        <Field label="Button label">
          <Input className={fieldClass} value={c.inapp.ctaLabel}
            onChange={(e) => update((d) => void (d.content.inapp.ctaLabel = e.target.value))} />
        </Field>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <Field label="Headline" required>
        <Input className={fieldClass} value={c.web.headline} placeholder="Members earn double this weekend"
          onChange={(e) => update((d) => void (d.content.web.headline = e.target.value))} />
      </Field>
      <Field label="Text">
        <Textarea className="min-h-[80px] bg-white" value={c.web.body}
          onChange={(e) => update((d) => void (d.content.web.body = e.target.value))} />
      </Field>
      <VariableChips onInsert={append((d, t) => void (d.content.web.body += ` ${t}`))} />
      <Field label="Button label">
        <Input className={fieldClass} value={c.web.ctaLabel}
          onChange={(e) => update((d) => void (d.content.web.ctaLabel = e.target.value))} />
      </Field>
    </div>
  );
}

function ChannelsStep({ draft, update, ctx }: StepProps) {
  const [active, setActive] = useState<ChannelKey | null>(draft.channels[0] ?? null);
  const [sample, setSample] = useState(true);
  const estimate = estimateAudience(draft, ctx);
  const values = sampleValues(draft, ctx);
  const raw = Object.fromEntries(Object.keys(values).map((k) => [k, `{{${k}}}`]));
  const current = active && draft.channels.includes(active) ? active : draft.channels[0] ?? null;

  return (
    <div className="space-y-6">
      <Section title="Where should it go?" description="Each member gets it on every channel they have agreed to.">
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {CHANNELS.map((channel) => {
            const on = draft.channels.includes(channel.key);
            const reach = estimate.byChannel.find((c) => c.key === channel.key)?.members;
            return (
              <ChoiceCard
                key={channel.key}
                active={on}
                onClick={() => {
                  update((d) => void (d.channels = toggle(d.channels, channel.key)));
                  if (!on) setActive(channel.key);
                }}
                title={channel.label}
                hint={on && reach != null ? `${fmt(reach)} can receive it` : channel.hint}
                icon={CHANNEL_ICONS[channel.key]}
              />
            );
          })}
        </div>
      </Section>

      {current ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 pt-3 sm:px-6">
              <div className="flex gap-1 overflow-x-auto">
                {CHANNELS.filter((c) => draft.channels.includes(c.key)).map((channel) => {
                  const Icon = CHANNEL_ICONS[channel.key];
                  return (
                    <button
                      key={channel.key}
                      type="button"
                      onClick={() => setActive(channel.key)}
                      className={cn(
                        "inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 pb-3 text-sm font-medium",
                        current === channel.key
                          ? "border-green-600 text-green-700"
                          : "border-transparent text-slate-500 hover:text-slate-800",
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {channel.label}
                    </button>
                  );
                })}
              </div>
              <label className="mb-3 flex items-center gap-2 text-xs text-slate-600">
                <Switch checked={sample} onCheckedChange={setSample} aria-label="Preview with sample member" />
                Preview as sample member
              </label>
            </div>
            <div className="grid gap-6 p-4 sm:p-6 xl:grid-cols-[minmax(0,1fr)_320px]">
              <ChannelEditor channel={current} draft={draft} update={update} ctx={ctx} />
              <div className="xl:sticky xl:top-4 xl:self-start">
                <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <Eye className="h-3.5 w-3.5" />
                  Live preview
                </p>
                <ChannelPreview channel={current} draft={draft} ctx={ctx} values={sample ? values : raw} />
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function ScheduleStep({ draft, update, ctx }: StepProps) {
  const s = draft.schedule;
  return (
    <div className="space-y-6">
      <Section title="When should it go out?">
        <div className="grid gap-2 sm:grid-cols-3">
          <ChoiceCard active={s.mode === "once"} onClick={() => update((d) => void (d.schedule.mode = "once"))}
            title="Once" hint="At a date and time" icon={Send} />
          <ChoiceCard active={s.mode === "recurring"} onClick={() => update((d) => void (d.schedule.mode = "recurring"))}
            title="Repeating" hint="Daily, weekly or monthly" icon={Repeat} />
          <ChoiceCard active={s.mode === "event"} onClick={() => update((d) => void (d.schedule.mode = "event"))}
            title="On an event" hint="When a member does something" icon={Zap} />
        </div>

        {s.mode === "event" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Trigger" required>
              <Select value={s.triggerEvent} onValueChange={(v) => update((d) => void (d.schedule.triggerEvent = v))}>
                <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRIGGER_EVENTS.map((event) => (
                    <SelectItem key={event.value} value={event.value}>{event.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Wait before sending" hint="0 sends right away">
              <div className="flex items-center gap-2">
                <Input className={fieldClass} type="number" min={0} value={s.delayHours}
                  onChange={(e) => update((d) => void (d.schedule.delayHours = e.target.value))} />
                <span className="text-sm text-muted-foreground">hours</span>
              </div>
            </Field>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <Field label={s.mode === "once" ? "Send date" : "First send"} required>
              <Input className={fieldClass} type="date" value={s.date}
                onChange={(e) => update((d) => void (d.schedule.date = e.target.value))} />
            </Field>
            <Field label="Time" required>
              <Input className={fieldClass} type="time" value={s.time}
                onChange={(e) => update((d) => void (d.schedule.time = e.target.value))} />
            </Field>
            {s.mode === "recurring" ? (
              <Field label="Repeat">
                <Select value={s.recurrence}
                  onValueChange={(v) => update((d) => void (d.schedule.recurrence = v as typeof s.recurrence))}>
                  <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Every day</SelectItem>
                    <SelectItem value="weekly">Every week</SelectItem>
                    <SelectItem value="monthly">Every month</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            ) : null}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Time zone">
            <Select value={s.timeZone} onValueChange={(v) => update((d) => void (d.schedule.timeZone = v))}>
              <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIME_ZONE_OPTIONS.map((zone) => (
                  <SelectItem key={zone.value} value={zone.value}>{zone.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Campaign ends" hint="The reward rule and repeats stop after this date">
            <Input className={fieldClass} type="date" value={s.endDate}
              onChange={(e) => update((d) => void (d.schedule.endDate = e.target.value))} />
          </Field>
        </div>
      </Section>

      <Section title="Guardrails">
        <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center gap-3">
            <Moon className="h-5 w-5 text-indigo-500" />
            <div>
              <p className="text-sm font-semibold text-slate-900">Respect quiet hours</p>
              <p className="text-xs text-muted-foreground">
                No messages between {ctx.quietHoursStart} and {ctx.quietHoursEnd}
              </p>
            </div>
          </div>
          <Switch checked={s.respectQuietHours} aria-label="Respect quiet hours"
            onCheckedChange={(checked) => update((d) => void (d.schedule.respectQuietHours = checked))} />
        </div>
        <Field label="Most messages a member gets per day" hint={`Program default is ${ctx.frequencyCap}`}>
          <Input className={cn(fieldClass, "max-w-[160px]")} type="number" min={1} value={s.frequencyCap}
            onChange={(e) => update((d) => void (d.schedule.frequencyCap = e.target.value))} />
        </Field>
      </Section>
    </div>
  );
}

function ReviewStep({ draft, ctx, goTo }: StepProps & { goTo: (step: StepId) => void }) {
  const estimate = estimateAudience(draft, ctx);
  const cost = estimateCost(draft, estimate.audience, ctx);
  const checks = reviewChecks(draft, ctx);
  const s = draft.schedule;
  const when =
    s.mode === "event"
      ? `When: ${TRIGGER_EVENTS.find((e) => e.value === s.triggerEvent)?.label ?? s.triggerEvent}${Number(s.delayHours) ? `, after ${s.delayHours}h` : ""}`
      : `${s.mode === "recurring" ? `Every ${{ daily: "day", weekly: "week", monthly: "month" }[s.recurrence]} from` : "On"} ${s.date} at ${s.time}`;

  const rows: { step: StepId; title: string; lines: string[] }[] = [
    {
      step: "basics",
      title: "Basics",
      lines: [
        draft.name || "Untitled campaign",
        `${draft.type || "No type"} · ${bonusLabel(draft, ctx)}`,
      ],
    },
    {
      step: "audience",
      title: "Audience",
      lines: [
        draft.audience.segments.length && !draft.audience.segments.includes(ALL_MEMBERS)
          ? ctx.segments.filter((x) => draft.audience.segments.includes(x.id)).map((x) => x.label).join(", ")
          : "All members",
        `${fmt(estimate.audience)} members · ${draft.audience.controlPct}% held out`,
      ],
    },
    {
      step: "channels",
      title: "Channels",
      lines: [
        CHANNELS.filter((c) => draft.channels.includes(c.key)).map((c) => c.label).join(", ") || "None",
        `${fmt(estimate.reachable)} members can receive it`,
      ],
    },
    {
      step: "schedule",
      title: "Schedule",
      lines: [when, `${s.timeZone}${s.endDate ? ` · ends ${s.endDate}` : ""}`],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        {rows.map((row) => (
          <Card key={row.step} className="border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{row.title}</p>
                <button type="button" onClick={() => goTo(row.step)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:underline">
                  <Pencil className="h-3 w-3" /> Edit
                </button>
              </div>
              <p className="mt-2 text-sm font-semibold text-slate-900">{row.lines[0]}</p>
              <p className="mt-0.5 text-sm text-muted-foreground">{row.lines[1]}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {draft.offer.enabled ? (
        <Section title="Points cost">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-slate-50 p-3">
              <p className="text-xs text-muted-foreground">Most points issued</p>
              <p className="mt-1 text-xl font-bold text-slate-900">{fmt(cost.points)}</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3">
              <p className="text-xs text-muted-foreground">Money value</p>
              <p className="mt-1 text-xl font-bold text-slate-900">{ctx.currency} {fmt(cost.money)}</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3">
              <p className="text-xs text-muted-foreground">Approval</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {cost.needsApproval ? "Second admin needed" : "Not needed"}
              </p>
            </div>
          </div>
        </Section>
      ) : null}

      <Section title="Checks">
        <ul className="space-y-2">
          {checks.map((check) => (
            <li key={check.message} className="flex items-start gap-2 text-sm">
              {!check.ok ? (
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
              ) : check.warn ? (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              )}
              <span className={cn(!check.ok ? "text-red-700" : "text-slate-700")}>{check.message}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Summary rail                                                        */
/* ------------------------------------------------------------------ */

function SummaryRail({ draft, ctx }: { draft: CampaignDraft; ctx: ProgramContext }) {
  const estimate = estimateAudience(draft, ctx);
  const cost = estimateCost(draft, estimate.audience, ctx);
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="space-y-4 p-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">At a glance</p>
          <p className="mt-1 truncate text-base font-semibold text-slate-900">{draft.name || "Untitled campaign"}</p>
          <p className="text-sm text-muted-foreground">{draft.type || "Type not set"} · {bonusLabel(draft, ctx)}</p>
        </div>
        <div className="rounded-xl bg-green-50 p-4">
          <p className="text-xs text-green-800">Audience</p>
          <p className="text-3xl font-bold tabular-nums text-green-900">{fmt(estimate.audience)}</p>
          <p className="text-xs text-green-800">{fmt(estimate.control)} held out as control</p>
        </div>
        {draft.channels.length ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reach by channel</p>
            {estimate.byChannel.map((channel) => {
              const Icon = CHANNEL_ICONS[channel.key];
              const pct = estimate.audience ? (channel.members / estimate.audience) * 100 : 0;
              return (
                <div key={channel.key}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5 text-slate-700"><Icon className="h-3.5 w-3.5" />{channel.label}</span>
                    <span className="tabular-nums text-slate-900">{fmt(channel.members)}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-slate-100">
                    <div className="h-1.5 rounded-full bg-green-600" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
        {draft.offer.enabled ? (
          <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-sm">
            <span className="text-slate-600">Max points cost</span>
            <span className="font-semibold tabular-nums text-slate-900">
              {fmt(cost.points)} · {ctx.currency} {fmt(cost.money)}
            </span>
          </div>
        ) : null}
        <div className="flex items-center gap-1.5 border-t border-slate-100 pt-3 text-xs text-muted-foreground">
          <CalendarClock className="h-3.5 w-3.5" />
          {draft.schedule.mode === "event" ? "Sends on an event" : `${draft.schedule.date || "No date"} · ${draft.schedule.time || "--:--"}`}
        </div>
        <p className="text-[11px] text-muted-foreground">Counts are estimates from the latest segment run.</p>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

type SaveStatus = "Draft" | "Scheduled" | "Pending approval";

export default function CampaignBuilder() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { programId } = useActiveProgramId();
  const programQuery = useQuery({
    queryKey: programKeys.detail(programId ?? ""),
    queryFn: () => ProgramsApi.get(programId!),
    enabled: Boolean(programId),
  });
  const segmentsQuery = useQuery({
    queryKey: insightKeys.segments(programId ?? ""),
    queryFn: () => InsightsApi.segments(programId!),
    enabled: Boolean(programId),
  });
  const campaignQuery = useQuery({
    queryKey: campaignKeys.detail(programId ?? "", id ?? ""),
    queryFn: () => CampaignsApi.get(programId!, id!),
    enabled: Boolean(programId && id),
  });
  const ctx = useMemo(
    () => programContext(programQuery.data, segmentsQuery.data),
    [programQuery.data, segmentsQuery.data],
  );

  const [draft, setDraft] = useState<CampaignDraft>(() => emptyDraft(ctx));
  const [draftReady, setDraftReady] = useState(!isEdit);
  const [step, setStep] = useState<StepId>("basics");
  const nextCode = nextCampaignCode([]);
  const [showErrors, setShowErrors] = useState(false);

  // Prefill once when create mounts / when edit detail arrives — do not depend on
  // `ctx` identity or the form will reset while the user types.
  useEffect(() => {
    if (isEdit) return;
    setDraft(emptyDraft(programContext(programQuery.data, segmentsQuery.data)));
    setDraftReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || !campaignQuery.data) return;
    setDraft(
      draftFromApiCampaign(
        campaignQuery.data,
        programContext(programQuery.data, segmentsQuery.data),
      ),
    );
    setDraftReady(true);
    setStep("basics");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, campaignQuery.data]);

  const apiStatus = campaignQuery.data?.status;
  const canEdit = !isEdit || isCampaignEditable(apiStatus);

  const update = (fn: (draft: CampaignDraft) => void) =>
    setDraft((prev) => {
      const next = structuredClone(prev);
      fn(next);
      return next;
    });

  const index = STEPS.findIndex((s) => s.id === step);
  const errors = stepErrors(step, draft, ctx);
  const doneSteps = new Set(
    STEPS.filter((s) => s.id !== "review" && stepErrors(s.id, draft, ctx).length === 0).map((s) => s.id),
  );
  const canReach = (target: StepId) => {
    const targetIndex = STEPS.findIndex((s) => s.id === target);
    return STEPS.slice(0, targetIndex).every((s) => doneSteps.has(s.id));
  };
  const allValid = STEPS.filter((s) => s.id !== "review").every((s) => doneSteps.has(s.id));
  const cost = estimateCost(draft, estimateAudience(draft, ctx).audience, ctx);

  const goTo = (target: StepId) => {
    if (target === step) return;
    const targetIndex = STEPS.findIndex((s) => s.id === target);
    if (targetIndex > index && !canReach(target)) {
      setShowErrors(true);
      toast.error("Finish the earlier steps first.");
      return;
    }
    setShowErrors(false);
    setStep(target);
    window.scrollTo({ top: 0 });
  };

  const next = () => {
    if (errors.length) {
      setShowErrors(true);
      return;
    }
    goTo(STEPS[index + 1].id);
  };

  const finish = (message: string) => {
    queryClient.invalidateQueries({ queryKey: campaignKeys.all });
    if (id) {
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(programId ?? "", id),
      });
    }
    toast.success(message);
    navigate("/perkpoint/rewards/campaigns");
  };

  const createMutation = useMutation({
    mutationFn: ({ status }: { status: SaveStatus }) =>
      CampaignsApi.create(
        programId!,
        draftToCampaignPayload(draft, ctx, {
          onlyCompleteSteps: status === "Draft",
        }),
      ),
    onSuccess: (created, { status }) => {
      finish(
        status === "Draft"
          ? `${created.name || draft.name || "Campaign"} saved as draft`
          : status === "Pending approval"
            ? `${created.name || draft.name} sent for approval`
            : `${created.name || draft.name} scheduled`,
      );
    },
    onError: (error) =>
      toast.error(apiErrorMessage(error, "Could not create the campaign")),
  });

  const updateMutation = useMutation({
    mutationFn: ({ status }: { status: SaveStatus }) =>
      CampaignsApi.update(
        programId!,
        id!,
        draftToCampaignPayload(draft, ctx, {
          // Full save on review/launch; draft save can omit incomplete steps.
          onlyCompleteSteps: status === "Draft",
        }),
      ),
    onSuccess: (updated) => {
      const movedToDraft =
        apiStatus === "pending_approval" || apiStatus === "scheduled";
      finish(
        movedToDraft
          ? `${updated.name || draft.name} updated and moved back to draft`
          : `${updated.name || draft.name} updated`,
      );
    },
    onError: (error) =>
      toast.error(apiErrorMessage(error, "Could not update the campaign")),
  });

  const submitMutation = useMutation({
    mutationFn: () => CampaignsApi.submit(programId!, id!),
    onSuccess: (submitted) => {
      finish(
        `${submitted.name || draft.name || "Campaign"} submitted for approval`,
      );
    },
    onError: (error) =>
      toast.error(apiErrorMessage(error, "Could not submit for approval")),
  });

  const saving =
    createMutation.isPending ||
    updateMutation.isPending ||
    submitMutation.isPending;

  const canSubmitForApproval =
    isEdit && (apiStatus || "").toLowerCase() === "draft";

  const persist = (status: SaveStatus) => {
    if (!programId) {
      toast.error("Open a program before saving a campaign.");
      return;
    }
    if (isEdit) {
      if (!canEdit) {
        toast.error("This campaign can no longer be edited.");
        return;
      }
      updateMutation.mutate({ status });
      return;
    }
    createMutation.mutate({ status });
  };

  const saveDraft = () => {
    if (!draft.name.trim()) {
      setStep("basics");
      setShowErrors(true);
      toast.error("Name the campaign before saving.");
      return;
    }
    persist("Draft");
  };

  const launch = () => {
    if (!allValid) {
      toast.error("Some steps still need attention.");
      return;
    }
    persist(cost.needsApproval ? "Pending approval" : "Scheduled");
  };

  const props: StepProps = { draft, update, ctx };

  if (isEdit && campaignQuery.isLoading) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <p className="text-sm text-muted-foreground">Loading campaign…</p>
      </div>
    );
  }

  if (isEdit && campaignQuery.isError) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 py-10">
        <p className="text-sm text-red-600">
          {apiErrorMessage(campaignQuery.error, "Could not load the campaign")}
        </p>
        <Button variant="outline" asChild>
          <Link to="/perkpoint/rewards/campaigns">Back to campaigns</Link>
        </Button>
      </div>
    );
  }

  if (isEdit && !canEdit) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 py-10">
        <h1 className="text-2xl font-bold text-slate-900">Cannot edit campaign</h1>
        <p className="text-sm text-muted-foreground">
          Status <span className="font-medium text-slate-800">{apiStatus}</span>{" "}
          is locked. Only draft, pending approval, scheduled, or paused campaigns
          can be edited.
        </p>
        <Button variant="outline" asChild>
          <Link to="/perkpoint/rewards/campaigns">Back to campaigns</Link>
        </Button>
      </div>
    );
  }

  if (!draftReady) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <p className="text-sm text-muted-foreground">Preparing form…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2 h-8 px-2 text-slate-500">
            <Link to="/perkpoint/rewards/campaigns"><ArrowLeft className="mr-1 h-4 w-4" />Campaigns</Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {isEdit ? "Edit campaign" : "Create campaign"}
          </h1>
          <p className="mt-1 text-muted-foreground">
            {ctx.name}
            {isEdit && draft.code ? ` · ${draft.code}` : ""}
            {isEdit &&
            (apiStatus === "pending_approval" || apiStatus === "scheduled")
              ? " · Saving will move this back to draft"
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="bg-white"
            onClick={() => toast.success("Test sent to your email and phone")}
          >
            <Send className="mr-1.5 h-4 w-4" /> Send test to myself
          </Button>
          {canSubmitForApproval ? (
            <Button
              type="button"
              className="bg-green-600 text-white hover:bg-green-700"
              disabled={saving || !programId || !id}
              onClick={() => {
                if (!allValid) {
                  toast.error(
                    "Finish all steps before submitting for approval.",
                  );
                  setStep("review");
                  setShowErrors(true);
                  return;
                }
                submitMutation.mutate();
              }}
            >
              {submitMutation.isPending
                ? "Submitting…"
                : "Submit for approval"}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Stepper */}
      <ol className="mt-5 flex gap-2 overflow-x-auto pb-1">
        {STEPS.map((s, i) => {
          const current = s.id === step;
          const done = doneSteps.has(s.id) && !current;
          return (
            <li key={s.id} className="flex min-w-[150px] flex-1 items-center gap-2">
              <button
                type="button"
                onClick={() => goTo(s.id)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
                  current ? "border-green-600 bg-green-50" : "border-slate-200 bg-white hover:bg-slate-50",
                )}
              >
                <span
                  className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    current ? "bg-green-600 text-white" : done ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500",
                  )}
                >
                  {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className="min-w-0">
                  <span className={cn("block text-sm font-semibold", current ? "text-green-800" : "text-slate-800")}>{s.label}</span>
                  <span className="block truncate text-xs text-muted-foreground">{s.hint}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-4">
          {showErrors && errors.length ? (
            <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-800">Before you continue</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-red-700">
                {errors.map((error) => <li key={error}>{error}</li>)}
              </ul>
            </div>
          ) : null}
          {step === "basics" ? <BasicsStep {...props} nextCode={nextCode} /> : null}
          {step === "audience" ? <AudienceStep {...props} /> : null}
          {step === "channels" ? <ChannelsStep {...props} /> : null}
          {step === "schedule" ? <ScheduleStep {...props} /> : null}
          {step === "review" ? <ReviewStep {...props} goTo={goTo} /> : null}
        </div>
        <div className="lg:sticky lg:top-4">
          <SummaryRail draft={draft} ctx={ctx} />
        </div>
      </div>

      {/* Action bar */}
      <div className="sticky bottom-0 z-20 -mx-5 mt-8 border-t border-slate-200 bg-white/95 px-5 py-3 backdrop-blur lg:-mx-7 lg:px-7">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2">
          <Button type="button" variant="outline" className="bg-white" disabled={index === 0}
            onClick={() => goTo(STEPS[index - 1].id)}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" className="bg-white" onClick={saveDraft} disabled={saving}>
              {isEdit ? "Save changes" : "Save draft"}
            </Button>
            {step === "review" ? (
              <Button type="button" className="bg-green-600 text-white hover:bg-green-700" onClick={launch}
                disabled={!allValid || saving}>
                {saving
                  ? "Saving…"
                  : isEdit
                    ? "Save & continue"
                    : cost.needsApproval
                      ? "Submit for approval"
                      : "Schedule campaign"}
              </Button>
            ) : (
              <Button type="button" className="bg-green-600 text-white hover:bg-green-700" onClick={next}>
                Continue <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
